import React, { useEffect, useState } from "react";

import "./CreateMcpServerDialog.scss";
import ModalDialog from "../../shared/ModalDialog";
import Spinner from "../../../assets/icons/Spinner";
import Message from "../../../i18n/Message";
import Notify from "../../shared/Notify";
import { PlusIcon } from "lucide-react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import label from "../../../i18n/label";
import backend from "../../../backend/backend";
import { linkToMcpServer } from "../../../routes";
import RadioButtons from "../../shared/radio/RadioButtons";
import urlHasSensitiveParam from "./urlKeyDetection";
import { MAX_AUTH_TOKEN_LENGTH } from "../playgroundLimits";
import { oauthStartErrorMessageId } from "../oauth/oauthStartError";
import PlaygroundAnalyzingView from "./PlaygroundAnalyzingView";
import usePlaygroundAnalysisPolling from "./usePlaygroundAnalysisPolling";

/**
 * Creates a playground, then keeps the user company while it is analyzed.
 *
 * @param analyzingUid mounts the dialog straight into its waiting state for that playground. This
 *        is how the OAuth path resumes: handing the browser to the authorization server destroys
 *        this component, so the callback returns to the list with ?analyzing=<uid> and the wait
 *        picks up where it left off.
 * @param onCreated reports a freshly created playground so the caller can put it in the URL, which
 *        is what makes a reload resume rather than drop the user on a bare list
 */
const CreateMcpServerDialog = ({ onClose, analyzingUid, onCreated }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [createdUid, setCreatedUid] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [name, setName] = useState("");
  const [nameInput, setNameInput] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteUrlElem, setWebsiteUrlElem] = useState(null);
  const [mcpServerUrl, setMcpServerUrl] = useState("");
  const [mcpServerUrlElem, setMcpServerUrlElem] = useState(null);

  const [formElem, setFormElem] = useState(null);

  // Auth UI state
  const AUTH_TYPE = {
    NONE: "NONE",
    BEARER_TOKEN: "BEARER_TOKEN",
    OAUTH: "OAUTH",
  };
  const [authType, setAuthType] = useState(AUTH_TYPE.NONE);
  const [bearerToken, setBearerToken] = useState("");
  const [bearerTokenElem, setBearerTokenElem] = useState(null);

  useEffect(() => {
    if (nameInput) nameInput.focus();
  }, [nameInput]);

  // The playground being waited on, whether this dialog created it a moment ago or the OAuth
  // callback pointed us at it. Either way the wait, and the dialog, look the same.
  const pollUid = analyzingUid || createdUid;
  const analyzing = !!pollUid;
  // Resuming after the OAuth round trip starts with `loading` false — the state died with the page
  // — so the submit button takes its cue from the wait itself, not only from this render's history.
  const busy = loading || analyzing;

  const { timedOut } = usePlaygroundAnalysisPolling(pollUid, {
    onFinished: (playground) => {
      // An errored playground still opens: its details page forces the settings tab, which is where
      // the bad URL or token is fixed. Without the toast the only sign is a small red badge.
      if (playground.status === "ERROR") {
        Notify.error(<Message id="mcp_playground_analysis_failed" />);
      }
      navigate(linkToMcpServer(playground.uid));
    },
    // Deleted mid-analysis, or a stale ?analyzing= from someone else's link: nothing to wait for,
    // and nothing the user did wrong, so close without a message.
    onMissing: () => onClose(),
  });

  const onCreate = () => {
    setValidating(false);
    setValidationError(false);
    const validName = nameInput.checkValidity();
    const validWebsite = websiteUrlElem.checkValidity();
    const validMcpServer = mcpServerUrlElem.checkValidity();
    const validBearer =
      authType !== AUTH_TYPE.BEARER_TOKEN || !bearerTokenElem
        ? true
        : bearerTokenElem.checkValidity();

    if (formElem) formElem.reportValidity();
    if (!validName || !validWebsite || !validMcpServer || !validBearer) return;
    setLoading(true);

    // `effectiveAuth` may differ from the current radio selection when the validator reports
    // that the server requires OAuth (see below) — pass it explicitly so the create request
    // and the branch both use the resolved auth type rather than the (possibly stale) state.
    const proceedCreating = (effectiveAuth) => {
      const req = {
        name,
        websiteUrl,
        mcpServerUrl,
        description: "",
        authType: effectiveAuth,
        authToken:
          effectiveAuth === AUTH_TYPE.BEARER_TOKEN ? bearerToken : "",
      };

      // OAuth: create the PENDING_AUTH playground, then kick off the provider authorization
      // and hand the browser over to it. No polling starts here — this page is about to be
      // replaced by the provider's. The wait resumes when the backend's callback returns the
      // browser to the list with ?analyzing=<uid> (see McpOAuthController#callback).
      if (effectiveAuth === AUTH_TYPE.OAUTH) {
        // Discovery and dynamic client registration talk to a third-party server before the
        // redirect can even be built, which is seconds of an apparently idle dialog. Say so.
        setRedirecting(true);
        backend
          .createPlayground(req)
          .then((resp) => backend.startMcpOAuth(resp.uid))
          .then((res) => {
            window.location.href = res.authorizationUrl;
          })
          .catch(async (err) => {
            console.error("Failed to start OAuth for playground ", req, err);
            // Surface the failure (e.g. the server doesn't support automatic OAuth registration)
            // instead of silently stopping. The orphaned PENDING_AUTH playground is swept by cron.
            setValidationError({
              message: label(
                intl,
                await oauthStartErrorMessageId(
                  err,
                  "create_playground_auth_oauth_start_error"
                )
              ),
            });
            setRedirecting(false);
            setLoading(false);
          });
        return;
      }

      backend
        .createPlayground(req)
        .then((resp) => {
          setCreatedUid(resp.uid);
          // Puts ?analyzing=<uid> in the URL, so a reload during the (5-10 minute) analysis comes
          // back to this waiting state instead of a bare list.
          onCreated?.(resp.uid);
        })
        .catch((err) => {
          console.error("Failed to create a playground ", req, err);
          setLoading(false);
        });
    };

    setValidating(true);
    backend
      .validateMcpServer(
        mcpServerUrl,
        authType,
        authType === AUTH_TYPE.BEARER_TOKEN ? bearerToken : ""
      )
      .then((resp) => {
        setValidating(false);
        if (resp.valid === true) {
          // The validator can detect that the server mandates OAuth; auto-select it so the
          // create request and UI reflect the requirement (the user could still change it back).
          let effectiveAuth = authType;
          if (resp.oauthRequired === true) {
            effectiveAuth = AUTH_TYPE.OAUTH;
            setAuthType(AUTH_TYPE.OAUTH);
          }
          proceedCreating(effectiveAuth);
        } else {
          setValidationError(resp);
          setLoading(false);
        }
      })
      .catch((err) => {
        setValidating(false);
        setValidationError(true);
        setLoading(false);
        console.error("Failed to validate MCP server", err);
      });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    onCreate();
  };

  return (
    <ModalDialog
      onClose={analyzing ? () => {} : onClose}
      className={
        "create_campaign_dialog create_mcp_server_dialog " +
        (analyzing ? "dark" : "")
      }
    >
      <div className="modal_title">
        <Message id="create_playground_dialog_title" />
      </div>
      <form onSubmit={onSubmit} ref={(val) => setFormElem(val)}>
        <div className="campaign_form">
          <div className="dialog_body">
            {!analyzing && (
              <>
                <div className="select_row">
                  <div className="label">
                    <Message id="create_playground_name" />
                  </div>
                  <input
                    className="form-control"
                    type="text"
                    ref={(val) => setNameInput(val)}
                    required
                    maxLength="120"
                    placeholder={label(
                      intl,
                      "create_playground_name_placeholder"
                    )}
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    readOnly={loading}
                  />
                </div>
                <div className="select_row">
                  <div className="label">
                    <Message id="create_playground_mcp_url" />
                  </div>
                  <div className="help">
                    <Message id="create_playground_mcp_url_help" />
                  </div>
                  <div className="help">
                    <Message id="create_playground_mcp_url_suggestion" />
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}{" "}
                    <a
                      role="button"
                      onClick={() => {
                        setMcpServerUrl("https://mcp.context7.com/mcp");
                      }}
                    >
                      <Message id="create_playground_mcp_url_suggestion_link" />
                    </a>
                  </div>
                  {urlHasSensitiveParam(mcpServerUrl) && (
                    <div className="help">
                      <Message id="create_playground_mcp_url_masked_hint" />
                    </div>
                  )}
                  <input
                    className="form-control"
                    type="url"
                    ref={(val) => setMcpServerUrlElem(val)}
                    required
                    placeholder={label(
                      intl,
                      "create_playground_mcp_url_placeholder"
                    )}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMcpServerUrl(value);
                      if (!value) {
                        // Reset auth selection when URL is cleared
                        setAuthType(AUTH_TYPE.NONE);
                      }
                    }}
                    value={mcpServerUrl}
                    readOnly={loading}
                  />
                  {(validating || redirecting || validationError) && (
                    <div className="progress_message">
                      {validating && (
                        <div className="progress_message_info">
                          <Message id="mcp_server_validation_in_progress" />
                        </div>
                      )}
                      {redirecting && (
                        <div className="progress_message_info">
                          <Message id="create_playground_redirecting_to_signin" />
                        </div>
                      )}
                      {validationError && (
                        <div className="progress_message_error">
                          <Message id="mcp_server_validation_error" />

                          {validationError.message && (
                            <>
                              <div className="validation_error_message_log_intro">
                                <Message id="mcp_server_validation_error_message_intro" />
                                :
                              </div>
                              <div className="validation_error_message_log">
                                {validationError.message}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="select_row">
                  <div className="label">
                    <Message id="create_playground_auth_type_label" />
                  </div>
                  <div className="pl-4">
                    <RadioButtons
                      options={[
                        {
                          value: AUTH_TYPE.NONE,
                          label: label(intl, "create_playground_auth_none"),
                        },
                        {
                          value: AUTH_TYPE.BEARER_TOKEN,
                          label: label(intl, "create_playground_auth_bearer"),
                        },
                        {
                          value: AUTH_TYPE.OAUTH,
                          label: label(intl, "create_playground_auth_oauth"),
                        },
                      ]}
                      value={authType}
                      onChange={(val) => setAuthType(val)}
                    />
                  </div>
                </div>

                {authType === AUTH_TYPE.BEARER_TOKEN && (
                  <div className="select_row">
                    <div className="label">
                      <Message id="create_playground_auth_token_label" />
                    </div>
                    <input
                      className="form-control"
                      type="text"
                      ref={(val) => setBearerTokenElem(val)}
                      required={authType === AUTH_TYPE.BEARER_TOKEN}
                      maxLength={MAX_AUTH_TOKEN_LENGTH}
                      placeholder={label(
                        intl,
                        "create_playground_auth_token_placeholder"
                      )}
                      onChange={(e) => setBearerToken(e.target.value)}
                      value={bearerToken}
                      readOnly={loading}
                    />
                  </div>
                )}
                {authType === AUTH_TYPE.OAUTH && (
                  <div className="select_row">
                    <div className="help">
                      <Message id="create_playground_auth_oauth_note" />
                    </div>
                  </div>
                )}
                <div className="select_row">
                  <div className="label">
                    <Message id="create_playground_website_url" />
                  </div>
                  <div className="help">
                    <Message id="create_playground_website_url_help" />
                  </div>
                  <input
                    className="form-control"
                    type="url"
                    ref={(val) => setWebsiteUrlElem(val)}
                    required
                    placeholder={label(
                      intl,
                      "create_playground_website_url_placeholder"
                    )}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    value={websiteUrl}
                    readOnly={loading}
                  />
                </div>
              </>
            )}
            {analyzing && <PlaygroundAnalyzingView timedOut={timedOut} />}
          </div>
        </div>
        <div className="buttons">
          {analyzing && timedOut ? (
            // The analysis has outlived any reasonable wait. Stop pretending the dialog is about
            // to close on its own and let the user go look at the playground itself.
            <button
              className="btn bg-gradient-dark"
              type="button"
              onClick={() => navigate(linkToMcpServer(pollUid))}
            >
              <Message id="playground_analysis_open_button" />
            </button>
          ) : (
            <button
              className="btn bg-gradient-dark"
              type="submit"
              disabled={busy}
            >
              {busy && <Spinner size="18" />}
              {!busy && <PlusIcon color="#fff" strokeWidth="2" size="18" />}
              {!analyzing && <Message id="create_playground_button" />}
              {analyzing && <Message id="analyzing_playground_button" />}
            </button>
          )}
          {!analyzing && (
            <button className="btn no-btn" onClick={onClose}>
              <Message id="cancel" />
            </button>
          )}
        </div>
      </form>
    </ModalDialog>
  );
};

export default CreateMcpServerDialog;
